import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { computeNavOverflow } from './nav-overflow';

type Refs = {
  /** The constrained container whose clientWidth is the available space. */
  containerRef: React.RefObject<HTMLElement>;
  /** Hidden, absolutely-positioned row holding ALL items at natural width. */
  measureRef: React.RefObject<HTMLElement>;
  /** Hidden element sized like the "…" trigger, for its reserved width. */
  triggerMeasureRef: React.RefObject<HTMLElement>;
};

/**
 * Recomputes `visibleCount` whenever the container resizes or the item set
 * (`depKey`) changes. Reads natural item widths from a hidden measurement row
 * so both shrink AND grow-back recompute correctly with no flash. All layout
 * reads happen in a layout effect / ResizeObserver callback; the pure decision
 * is delegated to computeNavOverflow.
 */
export function useNavOverflow(
  itemCount: number,
  depKey: string
): Refs & { visibleCount: number } {
  const containerRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLElement>(null);
  const triggerMeasureRef = useRef<HTMLElement>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    // The AVAILABLE width is the constrained grid `links` track — i.e. the host
    // element the slotted content lives in (`ps-nav-links`), NOT the inner
    // content box, which is centered/shrink-to-fit and would feed its own
    // collapsed width back in (runaway over-collapse). Fall back to the
    // container only if it has no parent yet.
    const track = container.parentElement ?? container;

    const itemWidths = Array.from(measure.children).map((child) => {
      const el = child as HTMLElement;
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      const marginInline =
        parseFloat(style.marginInlineStart || '0') +
        parseFloat(style.marginInlineEnd || '0');
      return rect.width + (Number.isFinite(marginInline) ? marginInline : 0);
    });

    const moreButtonWidth = triggerMeasureRef.current
      ? triggerMeasureRef.current.getBoundingClientRect().width
      : 0;

    const total = itemWidths.length;
    const { visibleCount: next } = computeNavOverflow({
      itemWidths,
      containerWidth: track.clientWidth,
      moreButtonWidth,
    });

    // Reserve room for the "…" trigger on the track (PSTUDIO-3720). When the
    // right-hand group + breadcrumb consume the row on a narrow viewport, the
    // 1fr links track legitimately collapses toward 0 — and then the overflow
    // container's `overflow: hidden` clips the "…" trigger itself, leaving ALL
    // collapsed tabs unreachable (the trigger is the only way into them). Pin a
    // min-width equal to the trigger whenever anything is collapsed, so the
    // track can never shrink below the "…". moreButtonWidth is a constant from
    // the hidden measure clone, so this converges; guard the write so it doesn't
    // feed the ResizeObserver a fresh size every tick (infinite loop).
    if (track instanceof HTMLElement) {
      const reserved =
        next < total && moreButtonWidth > 0
          ? `${Math.ceil(moreButtonWidth)}px`
          : '';
      if (track.style.minWidth !== reserved) {
        track.style.minWidth = reserved;
      }
    }

    setVisibleCount((prev) => (prev === next ? prev : next));
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    // Observe the TRACK (host), not the content box: when the track shrinks the
    // centered content box may keep its size, so observing the content would
    // miss the resize that actually triggers the collapse.
    const track = container.parentElement ?? container;
    recompute();

    // Settle guard (PSTUDIO-3720): the very first synchronous read can hit a
    // transient 0-width track — before the shadow `ps-universal-nav` mounts its
    // `navigation` slot, before the web font settles, before late presence
    // avatars widen the neighbouring max-content tracks. computeNavOverflow
    // treats that 0 as "show all" (never collapse), and these re-measurements
    // apply the real visibleCount once the track width actually lands, so tabs
    // never freeze in the "…" menu on a wide desktop.
    let cancelled = false;
    const settle = () => {
      if (!cancelled) recompute();
    };
    const raf = requestAnimationFrame(settle);
    // FontFaceSet.ready resolves once web fonts are applied (widths change).
    void document.fonts?.ready?.then(settle);

    const ro = new ResizeObserver(() => recompute());
    ro.observe(track);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      // Drop the reserved "…" min-width so a reused host (ps-nav-links persists
      // across solution switches) doesn't keep a stale reservation.
      if (track instanceof HTMLElement) track.style.minWidth = '';
    };
    // depKey / itemCount re-run measurement when the item labels/set change.
  }, [recompute, depKey, itemCount]);

  return { containerRef, measureRef, triggerMeasureRef, visibleCount };
}
